import strings from "global/constants/StringConstants";
import {
  AiTurn,
  ChatMessage,
  ContractCitation,
  ContractDocument,
  ContractMatchedDocument,
  ContractSearchResponse,
  MatchedContract,
  MatchedPassage,
} from "./ContractSearchTypes";

const copy = strings.contractSearch;

/** `{name}` placeholders → values. Missing keys are left in place so a typo shows up in the UI. */
export const fill = (
  template: string,
  values: Record<string, string | number>,
): string =>
  template.replace(/\{(\w+)\}/g, (match, key) =>
    key in values ? String(values[key]) : match,
  );

// ------------------------------------------------------------------------------------- documents

export const byDocumentId = (
  docs: ContractDocument[],
): Record<string, ContractDocument> =>
  docs.reduce<Record<string, ContractDocument>>((acc, doc) => {
    if (doc?.documentId) acc[doc.documentId] = doc;
    return acc;
  }, {});

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** A calendar date with no time of day: the AI's DATE columns, and `uploadedAt`. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** "14 Mar 2024" — the design's short date; "" for anything unparsable. */
export const formatShortDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  // A date-only string parses as UTC midnight, so the local getters below printed the day BEFORE
  // for anyone west of UTC -- "2026-01-01" read as "31 Dec 2025". Naming the time makes it local
  // midnight instead. A timestamp carries its own offset and is left exactly as it was.
  const date = new Date(DATE_ONLY.test(iso) ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

/** "Signed 14 Mar 2024 · 22 pages" / "Uploaded 8 Sep 2026 · 11 pages" — either half may be absent. */
export const documentMeta = (doc: ContractDocument | undefined): string => {
  if (!doc) return "";
  const parts: string[] = [];
  const signedAt = doc.signed ? doc.signatureDetails?.signedAt : undefined;
  const signedDate = formatShortDate(signedAt);
  const uploadedDate = formatShortDate(doc.createdAt);
  if (signedDate) parts.push(fill(copy.signedOn, { date: signedDate }));
  else if (uploadedDate)
    parts.push(fill(copy.uploadedOn, { date: uploadedDate }));
  if (doc.pageCount === 1) parts.push(copy.pageCountOne);
  else if (doc.pageCount && doc.pageCount > 1)
    parts.push(fill(copy.pageCount, { count: doc.pageCount }));
  return parts.join(" · ");
};

// ---------------------------------------------------------------------------------------- search

export const linesLabel = (
  lineStart: number | null | undefined,
  lineEnd: number | null | undefined,
): string => {
  if (lineStart == null) return "";
  if (lineEnd == null || lineEnd === lineStart) return `line ${lineStart}`;
  return `lines ${lineStart}–${lineEnd}`;
};

const passageFrom = (
  citation: ContractCitation,
  index: number,
  fileName: string,
): MatchedPassage => {
  const lines = linesLabel(citation.lineStart, citation.lineEnd);
  const pageLabel =
    citation.pageNumber != null ? `Page ${citation.pageNumber}` : "";
  return {
    key: `${citation.documentId}:${citation.chunkId ?? index}:${citation.pageNumber ?? ""}:${citation.lineStart ?? ""}`,
    documentId: citation.documentId,
    fileName,
    page: citation.pageNumber ?? null,
    lineStart: citation.lineStart ?? null,
    lineEnd: citation.lineEnd ?? null,
    linesLabel: lines,
    quote: citation.quote ?? "",
    label:
      citation.label ||
      [pageLabel, lines].filter(Boolean).join(", ") ||
      fileName,
  };
};

const fileNameFor = (
  documentId: string,
  matched: ContractMatchedDocument | undefined,
  citations: ContractCitation[],
  docs: Record<string, ContractDocument>,
  structured: Record<string, unknown>[],
): string => {
  if (matched?.documentName) return matched.documentName;
  const cited = citations.find((c) => c.documentId === documentId);
  if (cited?.documentName) return cited.documentName;
  if (docs[documentId]?.filename) return docs[documentId].filename;
  const row = structured.find((r) => r.documentId === documentId);
  if (typeof row?.filename === "string" && row.filename) return row.filename;
  return copy.untitledDocument;
};

/** "Uploaded 8 Sep 2026" from the matched row's own date, the last fallback for the meta line. */
const uploadedMeta = (uploadedAt: string | null | undefined): string => {
  const date = formatShortDate(uploadedAt);
  return date ? fill(copy.uploadedOn, { date }) : "";
};

/**
 * "Expires 30 Jun 2026 · Distribution Agreement" for a structured row when the documents list has
 * nothing better — the structured route returns the columns the question was about.
 */
const structuredMeta = (row: Record<string, unknown> | undefined): string => {
  if (!row) return "";
  const parts: string[] = [];
  const expiry = formatShortDate(
    typeof row.expiryDate === "string" ? row.expiryDate : null,
  );
  if (expiry) parts.push(`Expires ${expiry}`);
  if (typeof row.contractType === "string" && row.contractType)
    parts.push(row.contractType);
  return parts.join(" · ");
};

/**
 * Turns one AI response into what the thread renders.
 *
 * Kind: `blocked` → refusal; no citations and no matched documents → empty; otherwise answer. Rows
 * come from `matchedDocuments` (the AI's own order), passages from `citations` grouped by document.
 * A matched document with no citation is "Not used", and when fewer documents are cited than
 * matched the card shows the partial badge.
 */
export const toAiTurn = (
  response: ContractSearchResponse,
  docsById: Record<string, ContractDocument>,
): AiTurn => {
  const citations = response.citations ?? [];
  const matched = response.matchedDocuments ?? [];
  const structured = response.structuredResults ?? [];
  const suggested = (response.suggestedQuestions ?? []).filter(Boolean);

  if (response.route === "blocked") {
    return {
      kind: "refusal",
      answer: response.answer ?? "",
      contracts: [],
      usedCount: 0,
      matchedCount: 0,
      followUps: (suggested.length ? suggested : copy.refusalFollowUps).slice(
        0,
        4,
      ),
      requestId: response.requestId ?? null,
      searchLogId: response.searchLogId ?? null,
    };
  }

  // No passage cited: either nothing matched, or a conversational reply (a list, a count, a
  // clarifying question) whose suggestions continue the conversation rather than replace it.
  if (citations.length === 0 && matched.length === 0) {
    return {
      kind: "empty",
      answer: response.answer ?? "",
      contracts: [],
      usedCount: 0,
      matchedCount: 0,
      followUps: (suggested.length ? suggested : copy.fallbackFollowUps).slice(
        0,
        4,
      ),
      followUpsFromAi: suggested.length > 0,
      requestId: response.requestId ?? null,
      searchLogId: response.searchLogId ?? null,
    };
  }

  // Documents in the AI's matched order, plus any cited document it forgot to list.
  const orderedIds: string[] = [];
  const seen = new Set<string>();
  [...matched.map((m) => m.documentId), ...citations.map((c) => c.documentId)]
    .filter(Boolean)
    .forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        orderedIds.push(id);
      }
    });

  const contracts: MatchedContract[] = orderedIds.map((documentId) => {
    const matchedRow = matched.find((m) => m.documentId === documentId);
    const fileName = fileNameFor(documentId, matchedRow, citations, docsById, structured);
    const passages = citations
      .filter((c) => c.documentId === documentId)
      .map((c, i) => passageFrom(c, i, fileName));
    const doc = docsById[documentId];
    return {
      documentId,
      fileName,
      meta:
        documentMeta(doc) ||
        structuredMeta(structured.find((r) => r.documentId === documentId)) ||
        uploadedMeta(matchedRow?.uploadedAt),
      sourceRequestId: doc?.sourceRequestId ?? null,
      used: passages.length > 0,
      passages,
    };
  });

  const usedCount = contracts.filter((c) => c.used).length;
  return {
    kind: "answer",
    answer: response.answer ?? "",
    contracts,
    usedCount,
    matchedCount: contracts.length,
    followUps: (suggested.length ? suggested : copy.fallbackFollowUps).slice(
      0,
      4,
    ),
    requestId: response.requestId ?? null,
    searchLogId: response.searchLogId ?? null,
  };
};

// ------------------------------------------------------------------------------- answer markdown

/** One rendered block of an answer: a paragraph of inline runs, or a bold heading line. */
export interface AnswerBlock {
  kind: "heading" | "paragraph";
  /** Inline runs; `bold` runs are the `**...**` spans. Line breaks inside a paragraph are kept as "\n". */
  runs: { text: string; bold: boolean }[];
}

const BOLD_SPAN = /\*\*(.+?)\*\*/g;
const HEADING_LINE = /^\*\*(.+?)\*\*$/;

const splitRuns = (line: string): { text: string; bold: boolean }[] => {
  const runs: { text: string; bold: boolean }[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  BOLD_SPAN.lastIndex = 0;
  while ((m = BOLD_SPAN.exec(line)) !== null) {
    if (m.index > last) runs.push({ text: line.slice(last, m.index), bold: false });
    runs.push({ text: m[1], bold: true });
    last = m.index + m[0].length;
  }
  if (last < line.length) runs.push({ text: line.slice(last), bold: false });
  return runs;
};

/**
 * Parses the AI's light Markdown -- blank-line paragraphs and `**bold**` -- into blocks the card
 * renders. A line that is nothing but one bold span is a contract heading. Anything else in the
 * text (links, lists, HTML) is left as literal text, never interpreted.
 */
export const parseAnswer = (answer: string): AnswerBlock[] => {
  const text = (answer ?? "").replace(/\r\n?/g, "\n").trim();
  if (!text) return [];
  const blocks: AnswerBlock[] = [];
  for (const paragraph of text.split(/\n{2,}/)) {
    const lines = paragraph.split("\n").filter((l) => l.trim().length > 0);
    let pending: { text: string; bold: boolean }[] = [];
    const flush = () => {
      if (pending.length) blocks.push({ kind: "paragraph", runs: pending });
      pending = [];
    };
    lines.forEach((line) => {
      const heading = HEADING_LINE.exec(line.trim());
      if (heading) {
        flush();
        blocks.push({ kind: "heading", runs: [{ text: heading[1], bold: true }] });
        return;
      }
      if (pending.length) pending.push({ text: "\n", bold: false });
      pending.push(...splitRuns(line));
    });
    flush();
  }
  return blocks;
};

/** The answer without Markdown markers -- what "Copy answer" puts on the clipboard. */
export const plainAnswer = (answer: string): string =>
  (answer ?? "").replace(/\*\*(.+?)\*\*/g, "$1").trim();

/**
 * The message an error turn shows. The server explains a 4xx in words meant for the
 * user (for example "You don't have any contracts to search yet …" on a 404); anything
 * else -- a 5xx, a timeout, a network failure -- gets the generic "try again" text so
 * that no internal wording reaches the screen.
 */
export const errorTurnMessage = (error: unknown): string => {
  const failure = error as { statusCode?: number; message?: unknown } | null;
  const status = failure?.statusCode;
  const message = typeof failure?.message === "string" ? failure.message.trim() : "";
  if (status !== undefined && status >= 400 && status < 500 && message) {
    return message;
  }
  return copy.errorAnswer;
};

export const errorTurn = (error?: unknown): AiTurn => ({
  kind: "error",
  answer: error === undefined ? copy.errorAnswer : errorTurnMessage(error),
  contracts: [],
  usedCount: 0,
  matchedCount: 0,
  followUps: [],
  requestId: null,
  searchLogId: null,
});

export const passageTag = (contract: MatchedContract): string => {
  if (!contract.used) return copy.notUsed;
  return contract.passages.length === 1
    ? copy.passageOne
    : fill(copy.passages, { count: contract.passages.length });
};

export const matchingPassagesHeading = (count: number): string =>
  count === 1
    ? copy.matchingPassageOne
    : fill(copy.matchingPassages, { count });

// --------------------------------------------------------------------------------- persistence

const THREAD_KEY_PREFIX = "contractSearch.thread.";
const MAX_STORED_MESSAGES = 40;

/**
 * The thread survives navigating away and back within the tab, but not a new tab or a sign-out —
 * hence sessionStorage, keyed by user so two accounts in one tab never see each other's questions.
 * Every read/write is guarded: storage can be missing or full, and the screen must still work.
 */
export const loadThread = (userKey: string): ChatMessage[] => {
  try {
    const raw = sessionStorage.getItem(THREAD_KEY_PREFIX + userKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // A turn that was still "thinking" when the page unloaded has no answer to show.
    return parsed.filter(
      (m: ChatMessage) =>
        m && (m.role === "user" || (m.role === "ai" && m.turn && !m.thinking)),
    );
  } catch {
    return [];
  }
};

export const saveThread = (userKey: string, messages: ChatMessage[]) => {
  try {
    const settled = messages
      .filter((m) => m.role === "user" || (m.turn && !m.thinking))
      .slice(-MAX_STORED_MESSAGES);
    sessionStorage.setItem(
      THREAD_KEY_PREFIX + userKey,
      JSON.stringify(settled),
    );
  } catch {
    // Quota or private mode: the thread simply does not persist.
  }
};

export const clearThread = (userKey: string) => {
  try {
    sessionStorage.removeItem(THREAD_KEY_PREFIX + userKey);
  } catch {
    // ignore
  }
};

/**
 * The AI's conversation id lives next to the thread it belongs to: same store, same per-user key, so
 * coming back to the screen continues the conversation and another account never inherits it.
 */
const CONVERSATION_KEY_PREFIX = "contractSearch.conversation.";

/** The shape of an id the server issues (a UUID, any case) -- the same check the server applies. */
const CONVERSATION_ID_SHAPE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isConversationId = (id: unknown): id is string =>
  typeof id === "string" && CONVERSATION_ID_SHAPE.test(id);

/**
 * True when the server refused the request because of the conversation id itself (its 400 names
 * `conversationId`), as opposed to a problem with the question.
 */
export const isBadConversationIdError = (error: any): boolean =>
  error?.statusCode === 400 &&
  typeof error?.message === "string" &&
  error.message.includes("conversationId");

export const loadConversationId = (userKey: string): string | null => {
  try {
    const stored = sessionStorage.getItem(CONVERSATION_KEY_PREFIX + userKey);
    // A damaged value would be refused by the server on every question; start fresh instead.
    if (stored && !isConversationId(stored)) {
      sessionStorage.removeItem(CONVERSATION_KEY_PREFIX + userKey);
      return null;
    }
    return stored || null;
  } catch {
    return null;
  }
};

export const saveConversationId = (userKey: string, id: string | null) => {
  try {
    if (id) sessionStorage.setItem(CONVERSATION_KEY_PREFIX + userKey, id);
    else sessionStorage.removeItem(CONVERSATION_KEY_PREFIX + userKey);
  } catch {
    // Storage missing or full: the conversation just will not survive leaving the screen.
  }
};

/**
 * Counts how many times the conversation was thrown away (New search, logout, login). A request
 * remembers the value it started under; if it changed by the time the answer arrives, the answer
 * belongs to a conversation that no longer exists and must not touch the id or the thread.
 */
let sessionEpoch = 0;
export const currentSessionEpoch = (): number => sessionEpoch;
export const bumpSessionEpoch = (): number => (sessionEpoch += 1);

/** Forget every stored contract-search thread and conversation id, for every user key in this tab. */
export const resetContractSearchSession = () => {
  bumpSessionEpoch();
  try {
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key.startsWith(THREAD_KEY_PREFIX) ||
          key.startsWith(CONVERSATION_KEY_PREFIX))
      ) {
        stale.push(key);
      }
    }
    stale.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was persisted, so nothing to clear.
  }
};

let messageSequence = 0;
export const nextMessageId = (): string =>
  `${Date.now().toString(36)}-${(messageSequence += 1)}`;
